import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useResetRecoilState } from "recoil";
import { useMutation } from "@apollo/react-hooks";
import {
  Box,
  Collapse,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { InlineIcon } from "@iconify/react";
import axios from "axios";
import {
  MyCafeteriaCostsBox,
  MyCafeteriaCurrentItemIndicator,
  MyCafeteriaMenuSelect,
  MyCafeteriaPlanSwitch,
} from "my-cafeteria/components";
import {
  MY_CAFETERIA_ADD_STATEMENT_ITEM,
  MY_CAFETERIA_DELETE_STATEMENT_ITEM,
  MY_CAFETERIA_UPDATE_STATEMENT_ITEM,
  MY_CAFETERIA_GET_CURRENT_STATEMENT_ITEMS,
  MY_CAFETERIA_GET_CURRENT_STATEMENT,
} from "my-cafeteria/graphql";
import { useMyCafeteriaMenu } from "my-cafeteria/hooks";
import {
  myCafateriaNewStatementItemState,
  myCafeteriaAddItemResultState,
} from "my-cafeteria/states";
import { OcCardButtonBar, OcMonthSelect } from "oc/components";
import { useOcApi } from "oc/hooks/use-api";
import useOcButtonGroup from "oc/hooks/use-button-group";
import { debounce } from "oc/utils/debounce";
import { useOcSnackbar } from "oc/hooks";

interface MyCafeteriaBenefitFormProps {
  onCloseForm: () => void;
  contractCode: string;
  inlineEdit?: boolean;
  row?: any;
  currentStatement?: any;
  currentStatementItems?: any[];
  isMobile: boolean;
}

export const MyCafeteriaBenefitForm: React.FC<MyCafeteriaBenefitFormProps> = ({
  onCloseForm,
  contractCode,
  inlineEdit = false,
  row,
  currentStatement,
  currentStatementItems,
  isMobile,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { setOpenMessage } = useOcSnackbar();

  let benefitIndicator = currentStatement?.allBenefits;
  let costIndicator = currentStatement?.allCosts;
  let availableAmountIndicator = currentStatement?.availableAmount;

  const { apiUrl } = useOcApi();
  const url = `${apiUrl}/calculate`;

  const [buttonDisabled, setButtonDisabled] = React.useState(false);
  const isSubmittingRef = useRef(false);
  const [initialCalculated, setInitialCalculated] =
    React.useState<boolean>(false);
  const [hasValidCalculation, setHasValidCalculation] = React.useState(false);
  const lastCalculatedAmountRef = useRef<number | string | null>(null);

  const [errorState, setErrorState] = useRecoilState(
    myCafeteriaAddItemResultState,
  );
  const resetErrorState = useResetRecoilState(myCafeteriaAddItemResultState);

  const buttonsDef = ["MY_CAFETERIA_NETTO", "MY_CAFETERIA_BRUTTO"];
  const { OcButtonGroup, selectedButton, handleButtonChange } =
    useOcButtonGroup({
      id: `MyCafeteriaBenefit`,
      buttonsDef,
    });

  const [newItem, setNewItem] = useRecoilState(
    myCafateriaNewStatementItemState,
  );
  const resetNewItemState = useResetRecoilState(
    myCafateriaNewStatementItemState,
  );

  const [calculatedBenefit, setCalculatedBenefit] = React.useState(() => {
    if (inlineEdit) {
      return {
        benefit: row?.amount,
        cost: row?.cost,
      };
    } else {
      return {
        benefit: 0,
        cost: 0,
      };
    }
  });

  const [addCafeteriaStatement] = useMutation(MY_CAFETERIA_ADD_STATEMENT_ITEM, {
    refetchQueries: [
      {
        query: MY_CAFETERIA_GET_CURRENT_STATEMENT_ITEMS,
        variables: { contractCode },
      },
      {
        query: MY_CAFETERIA_GET_CURRENT_STATEMENT,
        variables: { contractCode },
      },
    ],
    onCompleted: () => {
      resetNewItemState();
      setButtonDisabled(false);
      isSubmittingRef.current = false;
      lastCalculatedAmountRef.current = null;
      onCloseForm();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setOpenMessage({
        type: "success",
        message: t("OC_OPERATION_SUCCESSFUL"),
      });
    },
    onError: (error) => {
      console.log({ error });
      let messages;
      try {
        messages = JSON.parse(error.message);
      } catch {
        messages = [error.message || t("OC_OPERATION_UNSUCCESSFUL")];
      }
      setErrorState({
        result: true,
        messages: messages,
      });
      setOpenMessage({
        type: "error",
        message: t("OC_OPERATION_UNSUCCESSFUL"),
      });
      setButtonDisabled(false);
      isSubmittingRef.current = false;
    },
  });

  const [deleteCafeteriaStatement] = useMutation(MY_CAFETERIA_DELETE_STATEMENT_ITEM, {
    refetchQueries: [
      {
        query: MY_CAFETERIA_GET_CURRENT_STATEMENT_ITEMS,
        variables: { contractCode },
      },
      {
        query: MY_CAFETERIA_GET_CURRENT_STATEMENT,
        variables: { contractCode },
      },
    ],
    onCompleted: () => {
      resetNewItemState();
      setButtonDisabled(false);
      isSubmittingRef.current = false;
      lastCalculatedAmountRef.current = null;
      onCloseForm();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setOpenMessage({
        type: "success",
        message: t("OC_OPERATION_SUCCESSFUL"),
      });
    },
    onError: (error) => {
      console.log({ error });
      let messages;
      try {
        messages = JSON.parse(error.message);
      } catch {
        messages = [error.message || t("OC_OPERATION_UNSUCCESSFUL")];
      }
      setErrorState({
        result: true,
        messages: messages,
      });
      setOpenMessage({
        type: "error",
        message: t("OC_OPERATION_UNSUCCESSFUL"),
      });
      setButtonDisabled(false);
      isSubmittingRef.current = false;
    },
  },
  );

  const [updateCafeteriaStatement] = useMutation(
    MY_CAFETERIA_UPDATE_STATEMENT_ITEM,
    {
      refetchQueries: [
        {
          query: MY_CAFETERIA_GET_CURRENT_STATEMENT_ITEMS,
          variables: { contractCode },
        },
        {
          query: MY_CAFETERIA_GET_CURRENT_STATEMENT,
          variables: { contractCode },
        },
      ],
      onCompleted: () => {
        resetNewItemState();
        setButtonDisabled(false);
        isSubmittingRef.current = false;
        onCloseForm();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setOpenMessage({
          type: "success",
          message: t("OC_OPERATION_SUCCESSFUL"),
        });
      },
      onError: (error) => {
        console.log({ error });
        setErrorState({
          result: true,
          messages: JSON.parse(error.message),
        });
        setOpenMessage({
          type: "error",
          message: t("OC_OPERATION_UNSUCCESSFUL"),
        });

        setButtonDisabled(false);
        isSubmittingRef.current = false;
      },
    },
  );

  const { data: cafeteriaMenuData } = useMyCafeteriaMenu({ contractCode });
  const selectedMenuItem = cafeteriaMenuData?.find(
    (item: { code: string }) => item.code === newItem.benefitType,
  );

  const filteredArray = ({
    start,
    end,
    array,
  }: {
    start: string;
    end: string;
    array: string[];
  }) => {
    if (!start || !end || !array) return [];
    const startMonth = start.slice(4, 6);
    const endMonth = end.slice(4, 6);
    const filtered = array.filter((item) => {
      if (item >= startMonth && item <= endMonth) {
        return true;
      }
      return false;
    });
    return filtered;
  };

  const availableMonthsArray = filteredArray({
    start: currentStatement?.startDate,
    end: currentStatement?.endDate,
    array: selectedMenuItem?.availableMonths,
  });

  const handleCancel = () => {
    setInitialCalculated(false);
    setHasValidCalculation(false);
    lastCalculatedAmountRef.current = null;
    onCloseForm();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    handleButtonChange(0);
    setTimeout(() => {
      resetNewItemState();
      resetErrorState();
      if (inlineEdit) {
        setCalculatedBenefit({
          benefit: row?.amount,
          cost: row?.cost,
        });
      } else {
        setCalculatedBenefit({
          benefit: 0,
          cost: 0,
        });
      }
    }, 500);
  };

  const formatDateFromString = (dateString: string) => {
    if (!dateString || dateString.length !== 8) return null;
    const year = dateString.slice(0, 4);
    const month = Number.parseInt(dateString.slice(4, 6), 10) - 1 || 0;
    const day = Number.parseInt(dateString.slice(6, 8), 10) || 1;
    return new Date(Number(year), month, day);
  };

  const getCalculatedAmounts = async ({
    item,
    currentStatement,
    onlyCheck,
  }: {
    item: {
      benefitType?: string;
      startDate?: string;
      endDate?: string;
      amount?: number | string;
      bruttoAmount?: number | string;
      contractCode?: string;
      reusable?: boolean;
      isReusable?: boolean;
      plan?: boolean;
      szepCardApplicationAmount?: number;
      isPeriodical?: boolean;
    };
    currentStatement?: {
      startDate?: string;
      endDate?: string;
    };
    onlyCheck?: boolean;
  }) => {
    if (!item?.benefitType || !item?.startDate) {
      return false;
    }

    const startMonthNum = Number(item.startDate);
    const endMonthNum = item.isPeriodical
      ? Number(item.endDate)
      : startMonthNum;

    const startParam = startMonthNum < 10
      ? `0${startMonthNum}`
      : String(startMonthNum);
    const endParam = endMonthNum < 10
      ? `0${endMonthNum}`
      : String(endMonthNum);

    const isReusable = item.reusable ?? item.isReusable ?? false;

    const params = {
      amount: Number(item.amount) || 0,
      bruttoAmount: Number(item.bruttoAmount) || 0,
      contractCode: item.contractCode,
      currentItemStartDate: currentStatement?.startDate,
      menuItemCode: item.benefitType,
      startDate: startParam,
      endDate: endParam,
      type: isReusable ? "repeating" : "oneTime",
      plan: item.plan ?? false,
      szepCardApplicationAmount: Number(item.szepCardApplicationAmount) || 0,
    };

    if (
      (!params.amount || params.amount === 0) &&
      (!params.bruttoAmount || params.bruttoAmount === 0)
    )
      return;
    if (!item?.isPeriodical) {
      params.endDate = params.startDate;
    }

    try {
      const { data } = await axios.get(url, { params });
      const calculated = data;
      const success = calculated.result;
      const messages = calculated.messages;

      if (onlyCheck && success) {
        resetErrorState();
        return true;
      }
      if (!success) {
        setErrorState({ result: true, messages });
        setHasValidCalculation(false);
        return false;
      }

      const result = calculated.resultObject;

      const start = formatDateFromString(result?.startDate);
      const end = formatDateFromString(result?.endDate);

      if (!start || !end) {
        console.error("Invalid dates");
        return false;
      }

      const totalMonths =
        (end.getFullYear() - start.getFullYear()) * 12 +
        (end.getMonth() - start.getMonth()) +
        1;

      if (totalMonths <= 0) {
        console.error("Invalid month range");
        return false;
      }

      const benefitPerMonth = result?.amount;
      const costPerMonth = result?.cost - result?.amount * totalMonths;

      if (item.amount === "") {
        setCalculatedBenefit({
          benefit: benefitPerMonth * totalMonths,
          cost: costPerMonth,
        });
        setNewItem((prevState: typeof newItem) => {
          const newState = { ...prevState };
          newState.amount = result.amount;
          newState.bruttoAmount = result.cost;
          newState.totalCost = costPerMonth;
          return newState;
        });
      } else {
        const totalBenefit = benefitPerMonth * totalMonths;
        const totalCost = costPerMonth;

        setCalculatedBenefit({
          benefit: totalBenefit,
          cost: totalCost,
        });

        setNewItem((prevState: typeof newItem) => {
          const newState = { ...prevState };
          newState.amount = result.amount;
          newState.bruttoAmount = result.cost;
          newState.totalCost = totalCost;
          return newState;
        });
      }
      resetErrorState();
      setHasValidCalculation(true);
      return success;
    } catch (error: any) {
      console.error('Calculate amounts API error:', error);
      // Handle timeout or network errors
      const errorMessage = error?.response?.data?.errors?.[0]?.message ||
        error?.message ||
        "Calculation service unavailable";
      setErrorState({
        result: true,
        messages: [errorMessage]
      });
      setHasValidCalculation(false);
      return false;
    }
  };

  const sendRequest = useCallback((value: typeof newItem) => {
    getCalculatedAmounts({ item: value, currentStatement, onlyCheck: false });
    // eslint-disable-next-line
  }, []);

  const debouncedCalculate = useMemo(() => {
    return debounce(sendRequest);
  }, [sendRequest]);

  const handleChange = (property: Partial<typeof newItem>) => {
    const newValue = { ...newItem, ...property };
    setNewItem((state: any) => ({ ...state, ...property }));
    if (property.amount || property.bruttoAmount) {
      setHasValidCalculation(false);
      debouncedCalculate(newValue);
    }
  };

  const handleSave = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setButtonDisabled(true);

    const szepCardAmount = Number(newItem?.szepCardApplicationAmount) || -1;

    let numberOfMonths = 1;
    if (newItem?.endDate) {
      numberOfMonths =
        Number.parseInt(newItem?.endDate, 10) - Number.parseInt(newItem?.startDate, 10) + 1;
    }

    const startDate = String(newItem?.startDate).padStart(2, "0");
    const endDate =
      numberOfMonths === 1
        ? startDate
        : String(newItem.endDate).padStart(2, "0");

    const newStatementItem = {
      contractCode,
      currentItemStartDate: currentStatement?.startDate,
      menuItemCode: newItem?.benefitType,
      startDate: startDate,
      endDate: endDate,
      type: newItem?.isReusable ? "repeating" : "oneTime",
      amount: Number.parseFloat(String(newItem?.amount)),
      bruttoAmount:
        selectedButton === "MY_CAFETERIA_BRUTTO"
          ? Number.parseFloat(String(newItem?.bruttoAmount))
          : 0,
      plan: newItem?.plan ? newItem?.plan : false,
      szepCardApplicationAmount: szepCardAmount,
    };

    let errorMessages: string[] = [];
    if (!newStatementItem?.menuItemCode)
      errorMessages.push(t("MY_CAFETERIA_MANDATORY_BENEFIT"));
    if (newItem?.isPeriodical) {
      if (!newStatementItem?.startDate)
        errorMessages.push(t("MY_CAFETERIA_MANDATORY_STARTING_MONTH"));
      if (!newStatementItem?.endDate)
        errorMessages.push(t("MY_CAFETERIA_MANDATORY_CLOSING_MONTH"));
    } else {
      if (!newStatementItem?.startDate)
        errorMessages.push(t("MY_CAFETERIA_MANDATORY_MONTH"));
    }
    if (!newItem?.amount) {
      errorMessages.push(t("MY_CAFETERIA_MANDATORY_VALUE"));
    }

    const overlap = currentStatementItems?.find(item => {
      const [s, e] = (item.months || "").split(" - ");
      return (
        item.menuItem.code === newStatementItem.menuItemCode &&
        startDate >= s && endDate <= e
      );
    });
    if (overlap) errorMessages.push(t("MY_CAFETERIA_APPLIED_BENEFIT"));

    if (errorMessages.length > 0) {
      setErrorState({ result: true, messages: errorMessages });
      setButtonDisabled(false);
      isSubmittingRef.current = false;
      return;
    }

    const isOK = await getCalculatedAmounts({
      item: { ...newItem, startDate: startDate, endDate: endDate },
      currentStatement,
      onlyCheck: true,
    });
    if (isOK) {
      addCafeteriaStatement({ variables: newStatementItem });
    } else {
      setButtonDisabled(false);
      isSubmittingRef.current = false;
    }
  };

  const handleUpdate = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setButtonDisabled(true);

    const startDate = String(newItem.startDate).padStart(2, "0");
    const endDate = String(newItem.endDate).padStart(2, "0");

    const params = {
      contractCode: newItem?.contractCode,
      currentItemStartDate: currentStatement?.startDate,
      menuItemCode: newItem?.menuItemCode,
      itemSerial: row?.itemSerial,
      startDate: startDate,
      endDate: endDate,
      type: newItem?.type,
      amount: parseFloat(String(newItem.amount)),
      szepCardApplicationAmount:
        newItem.szepCardApplicationAmount === row?.szepCardApplicationAmount
          ? -1
          : newItem.szepCardApplicationAmount,
      plan: row?.plan,
    };

    const isOK = await getCalculatedAmounts({
      item: { ...newItem, startDate: startDate, endDate: endDate },
      currentStatement,
      onlyCheck: true,
    });
    if (isOK) {
      updateCafeteriaStatement({ variables: params });
    } else {
      setButtonDisabled(false);
      isSubmittingRef.current = false;
    }
  };

  const handleDelete = () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setButtonDisabled(true);

    const params = {
      currentItemStartDate: currentStatement?.startDate,
      contractCode,
      menuItemCode: row?.menuItem?.code,
      startDate: String(row?.startDate).padStart(2, "0"),
      itemSerial: row?.itemSerial,
      endDate: String(row?.endDate).padStart(2, "0"),
      type: row?.type,
    };
    deleteCafeteriaStatement({ variables: params });
  };

  useEffect(() => {
    if (!newItem.contractCode) {
      setNewItem((prev: any) => ({ ...prev, contractCode }));
    }
  }, [contractCode, newItem.contractCode, setNewItem]);

  useEffect(() => {
    if (inlineEdit && row?.amount && row?.bruttoAmount) {
      setHasValidCalculation(true);
    }
  }, [inlineEdit, row?.amount, row?.bruttoAmount]);

  // Auto-calculate when copying from archive
  // This runs only once when contractCode becomes available
  useEffect(() => {
    // Only run if we have all required fields AND bruttoAmount is empty
    // This indicates a copy operation from archive, not manual input
    if (
      !inlineEdit &&
      newItem?.amount &&
      newItem?.benefitType &&
      newItem?.startDate &&
      newItem?.contractCode &&
      !newItem?.bruttoAmount &&
      lastCalculatedAmountRef.current !== newItem.amount &&
      // Additional check: ensure this looks like a copy operation (amount is set but brutto is not)
      newItem.amount !== "" &&
      Number(newItem.amount) > 0
    ) {
      lastCalculatedAmountRef.current = newItem.amount;
      getCalculatedAmounts({
        item: newItem,
        currentStatement,
        onlyCheck: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newItem?.contractCode]);

  const netto = newItem?.amount || 0;
  const brutto = newItem?.bruttoAmount || 0;
  if (
    (netto && !brutto && !initialCalculated) ||
    (brutto && !netto && !initialCalculated)
  ) {
    setInitialCalculated(true);
  }

  const isNewItemValid = () => {
    if (!errorState.result) return true;
    if (newItem?.isPeriodical) {
      return (
        newItem.benefitType &&
        newItem.startDate &&
        newItem.endDate &&
        newItem.amount &&
        newItem.bruttoAmount
      );
    } else {
      return (
        newItem.benefitType &&
        newItem.startDate &&
        newItem.amount &&
        newItem.bruttoAmount
      );
    }
  };

  let showSzepCardComponent = selectedMenuItem?.isSZEPCardItem;

  const [applicationState, setApplicationState] =
    React.useState("hasNotApplication");

  if (
    newItem?.szepCardApplicationAmount > 0 &&
    applicationState === "hasNotApplication"
  ) {
    setApplicationState("hasApplication");
  }

  const handleApplicationStateChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setApplicationState(event.target.value);
  };

  const thisYear = new Date().getFullYear().toString();
  let editBenefit = newItem?.bruttoAmount;

  if (inlineEdit) {
    benefitIndicator =
      currentStatement?.allBenefits - row?.amount + calculatedBenefit?.benefit;
    costIndicator =
      currentStatement?.allCosts - row?.cost + calculatedBenefit?.cost;
    availableAmountIndicator =
      currentStatement?.allAmount - benefitIndicator - costIndicator;
    editBenefit = row?.valueOfBenefit;
  } else {
    benefitIndicator =
      currentStatement?.allBenefits + calculatedBenefit?.benefit;
    costIndicator = currentStatement?.allCosts + calculatedBenefit?.cost;
    availableAmountIndicator =
      currentStatement?.allAmount - benefitIndicator - costIndicator;
  }

  const isOverlapedItem = currentStatementItems?.find(
    (item: any) =>
      (item?.menuItem?.code === newItem?.benefitType &&
        newItem.startDate >= item.startDate &&
        newItem.startDate <= item.endDate) ||
      (item?.menuItem?.code === newItem?.benefitType &&
        newItem.endDate >= item.startDate &&
        newItem.endDate <= item.endDate),
  );
  const isOverlap = isOverlapedItem && !inlineEdit ? true : false;

  const foundItem = cafeteriaMenuData?.find(
    (item: any) =>
      item?.isSZEPActiveHungariansCardItem === true &&
      item?.code === newItem?.benefitType,
  );


  return (
    <form>
      <Box
        style={{
          backgroundColor: "#F1F3F4",
          padding: "16px",
          borderRadius: "4px",
          border: "1px solid #E3E5E5",
        }}
      >
        <Typography variant="h6">
          {inlineEdit
            ? t("MY_CAFETERIA_EDIT_BENEFIT")
            : t("MY_CAFETERIA_ADD_NEW_BENEFIT")}
        </Typography>

        {cafeteriaMenuData?.length === 0 ? (
          <Box />
        ) : (
          <>
            <MyCafeteriaMenuSelect
              contractCode={contractCode}
              selectedItem={newItem?.benefitType}
              onSelectItem={(e) => {
                resetErrorState();
                handleChange({
                  benefitType: e.code,
                  isPeriodical: e.isPeriodical,
                  isReusable: e.isReusable,
                  isPlanable: e.planable,
                });
              }}
              disabled={inlineEdit}
              currentStatementItems={currentStatementItems}
              isMobile={isMobile}
            />

            <Box height="16px" />
            <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
              <OcMonthSelect
                id={`start-of-period-select-${row ? row.index : "zero"}`}
                label={
                  newItem.isPeriodical
                    ? t("MY_CAFETERIA_START_OF_PERIOD")
                    : t("OC_MONTH")
                }
                selectedMonth={newItem.startDate !== "" ? Number.parseInt(newItem.startDate, 10) : null}
                onSelectMonth={(e) => {
                  resetErrorState();
                  let newValue = Object.assign({}, newItem, {
                    startDate: e,
                    endDate: e,
                  });
                  handleChange({ startDate: e, endDate: e });
                  getCalculatedAmounts({
                    item: newValue,
                    currentStatement,
                    onlyCheck: false,
                  });
                }}
                monthList={availableMonthsArray.map((month) => ({
                  index: month,
                  name: month,
                }))}
                disabled={inlineEdit}
                startMonth={1}
                width="290px"
              />
              {newItem.isPeriodical && (
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  padding={isMobile ? "10px 0px" : "0px 22px"}
                >
                  <InlineIcon
                    icon="mdi:arrow-right"
                    width="24px"
                    color="#4d4d4d"
                    style={{
                      transform: isMobile ? "rotate(90deg)" : undefined,
                    }}
                  />
                </Box>
              )}
              {newItem.isPeriodical && (
                <OcMonthSelect
                  id={`end-of-period-select-${row ? row.index : "zero"}`}
                  label={t("MY_CAFETERIA_END_OF_PERIOD")}
                  selectedMonth={newItem.endDate !== "" ? parseInt(newItem.endDate, 10) : null}
                  onSelectMonth={(e) => {
                    let newValue = Object.assign({}, newItem, {
                      endDate: e,
                    });
                    handleChange({ endDate: e });
                    getCalculatedAmounts({
                      item: newValue,
                      currentStatement,
                      onlyCheck: false,
                    });
                  }}
                  monthList={availableMonthsArray.map((month) => ({
                    index: month,
                    name: month,
                  }))}
                  disabled={inlineEdit}
                  startMonth={newItem.startDate !== "" ? parseInt(newItem.startDate, 10) : 1}
                  width="290px"
                />
              )}
            </Box>
            <Box height="16px" />
            {foundItem && (
              <>
                <Box>
                  <Typography
                    variant="body1"
                    style={{
                      color: theme.palette.owGray.main,
                    }}
                  >
                    {t("MY_CAFETERIA_ACTIVE_HUNGARIANS_BENEFIT_INFO")}
                  </Typography>
                  {newItem?.isPlanable && <Box height="16px" />}
                </Box>
              </>
            )}
            <Collapse in={newItem?.isPlanable} timeout="auto" unmountOnExit>
              <Box
                width="auto"
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                alignItems="center"
              >
                <>
                  {isMobile ? (
                    <Box width="100%" display="flex">
                      <MyCafeteriaPlanSwitch
                        newItem={newItem}
                        handleChange={handleChange}
                      />
                    </Box>
                  ) : (
                    <MyCafeteriaPlanSwitch
                      newItem={newItem}
                      handleChange={handleChange}
                    />
                  )}
                </>
                {isMobile && <Box height="10px" />}
                <Box width="16px" />
                <Box>
                  <Typography
                    variant="body1"
                    style={{
                      color: theme.palette.owGray.main,
                    }}
                  >
                    {t("MY_CAFETERIA_CAFETERIA_PLAN")}
                  </Typography>
                </Box>
              </Box>
            </Collapse>
            <Box height="16px" />
            <Box
              style={{
                border: `1px solid ${theme.palette.gray40.main}`,
                borderRadius: "8px",
                padding: "16px",
              }}
            >
              <Box flexGrow={1} display="flex" flexDirection="row">
                <OcButtonGroup />
              </Box>

              <Box height="16px" />
              {selectedButton === "MY_CAFETERIA_NETTO" && (
                <Box
                  width={isMobile ? "auto" : "291px"}
                  style={{
                    backgroundColor: "white",
                    borderRadius: "4px",
                  }}
                >
                  <TextField
                    id={`my-cafeteria-benefit-amount-input-MY_CAFETERIA_NET_AMOUNT-${row ? row.index : "zero"
                      }`}
                    label={
                      newItem.isPeriodical
                        ? t("MY_CAFETERIA_MONTHLY_NET_AMOUNT")
                        : t("MY_CAFETERIA_NET_AMOUNT")
                    }
                    variant="outlined"
                    fullWidth
                    value={Math.round(newItem?.amount) || ""}
                    type="number"
                    onChange={(e) => {
                      let newValue = e.target.value
                        ? Number(e.target.value)
                        : "";
                      handleChange({ amount: newValue, bruttoAmount: "" });
                    }}
                    onFocus={resetErrorState}
                  />
                </Box>
              )}
              {selectedButton === "MY_CAFETERIA_BRUTTO" && (
                <Box
                  width={isMobile ? "auto" : "291px"}
                  style={{
                    backgroundColor: "white",
                    borderRadius: "4px",
                  }}
                >
                  <TextField
                    id={`my-cafeteria-benefit-amount-input-MY_CAFETERIA_GROSS_AMOUNT-${row ? row.index : "zero"
                      }`}
                    label={t("MY_CAFETERIA_GROSS_AMOUNT")}
                    variant="outlined"
                    fullWidth
                    value={
                      Math.round(newItem?.bruttoAmount) || editBenefit || ""
                    }
                    type="number"
                    onFocus={resetErrorState}
                    onChange={(e) => {
                      let newValue = e.target.value
                        ? Number(e.target.value)
                        : "";
                      handleChange({ bruttoAmount: newValue, amount: "" });
                    }}
                  />
                </Box>
              )}
              <Box>
                <Box height="16px" />
                <Box
                  display="flex"
                  flexDirection={isMobile ? "column" : "unset"}
                >
                  <MyCafeteriaCostsBox
                    isMobile={isMobile}
                    label={
                      newItem.isPeriodical
                        ? t("MY_CAFETERIA_MONTHLY_NET_AMOUNT")
                        : t("MY_CAFETERIA_NET_AMOUNT")
                    }
                    currencyValue={newItem?.amount || ""}
                  />
                  <Box width="16px" />
                  {isMobile && <Box height="16px" />}
                  <MyCafeteriaCostsBox
                    isMobile={isMobile}
                    label={t("MY_CAFETERIA_GROSS_AMOUNT")}
                    currencyValue={newItem?.bruttoAmount || editBenefit || ""}
                  />
                </Box>
              </Box>
              <Box height="16px" />
              <Typography variant="body1" style={{ color: "#5E6366" }}>
                {t("MY_CAFETERIA_MONTHLY_BRUTTO_INFO")}
              </Typography>
              <Typography variant="body1" style={{ color: "#5E6366" }}>
                {t("MY_CAFETERIA_BRUTTO_INFO")}
              </Typography>
            </Box>
          </>
        )}
        <Box height="16px" />
        <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
          <MyCafeteriaCurrentItemIndicator
            label={t("MY_CAFETERIA_USED_BENEFIT")}
            value={benefitIndicator}
            color="#2FA84F"
            width={isMobile ? "100%" : "33%"}
          />
          <Box width="16px" />
          {isMobile && <Box height="16px" />}
          <MyCafeteriaCurrentItemIndicator
            label={t("OC_COST")}
            value={costIndicator}
            color="#F3AA18"
            width={isMobile ? "100%" : "33%"}
          />
          <Box width="16px" />
          {isMobile && <Box height="16px" />}
          <MyCafeteriaCurrentItemIndicator
            label={t("MY_CAFETERIA_REMAINS")}
            value={availableAmountIndicator}
            color="#cc0033"
            width={isMobile ? "100%" : "33%"}
          />
        </Box>
        <Collapse in={showSzepCardComponent} timeout="auto" unmountOnExit>
          <Box
            style={{
              height: "16px",
              borderBottom: "1px solid #C6CACC",
            }}
          />
          <Box paddingTop="16px" color={theme.palette.gray80.main}>
            {t("MY_CAFETERIA_I_HAVE_BENEFIT", { thisYear })}
          </Box>
          <Box padding="16px 16px 0px 16px">
            <FormControl component="fieldset">
              <RadioGroup
                aria-label="application"
                value={applicationState}
                onChange={handleApplicationStateChange}
                name="radio-buttons-group"
              >
                <FormControlLabel
                  value="hasNotApplication"
                  control={
                    <Radio
                      id={`my-cafeteria-other-employer-1-${row ? row.index : "zero"
                        }`}
                    />
                  }
                  label={
                    <Typography
                      variant="body1"
                      color={theme.palette.gray80.main}
                    >
                      {t("MY_CAFETERIA_I_HAVE_NO_BENEFIT", { thisYear })}
                    </Typography>
                  }
                />
                {isMobile && <Box height="16px" />}
                <FormControlLabel
                  value="hasApplication"
                  control={
                    <Radio
                      id={`my-cafeteria-other-employer-2-${row ? row.index : "zero"
                        }`}
                    />
                  }
                  label={
                    <Typography
                      variant="body1"
                      color={theme.palette.gray80.main}
                    >
                      {t("MY_CAFETERIA_SZEP_BENEFIT_NAME", { thisYear })}
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>
          {isMobile && <Box height="16px" />}
          <Collapse
            in={applicationState === "hasApplication"}
            timeout="auto"
            unmountOnExit
          >
            <Box>
              <Box height="16px" />
              <Box
                width={isMobile ? "auto" : "291px"}
                style={{
                  backgroundColor: "white",
                  borderRadius: "4px",
                }}
              >
                <TextField
                  id={`my-cafeteria-other-employer-amount-${row ? row.index : "zero"
                    }`}
                  label={t("OC_AMOUNT")}
                  variant="outlined"
                  fullWidth
                  type="number"
                  value={newItem?.szepCardApplicationAmount ?? ""}
                  onChange={(e) => {
                    const val = e.target.value ? Number(e.target.value) : "";
                    handleChange({ szepCardApplicationAmount: val });
                    getCalculatedAmounts({
                      item: { ...newItem, szepCardApplicationAmount: val },
                      currentStatement,
                      onlyCheck: false,
                    });
                  }}
                />
              </Box>
              <Box height="16px" />
              <Typography variant="body1" color={theme.palette.gray80.main}>
                {t("MY_CAFETERIA_SUM_OTHER_BENEFIT")}
              </Typography>
            </Box>
          </Collapse>
        </Collapse>
        <Collapse
          in={errorState.result || availableAmountIndicator < 0 || isOverlap}
          timeout="auto"
          unmountOnExit
        >
          <Box id="error-box" color="#cc0033" paddingTop="16px">
            <Box id="error-description" paddingLeft="6px">
              {errorState?.messages?.map((message: string, index: number) => (
                <Typography
                  key={`error-message-${index}}`}
                  variant="body1"
                  color={theme.palette.primary.main}
                >
                  {message}
                </Typography>
              ))}
            </Box>
            {availableAmountIndicator < 0 && (
              <Box paddingLeft="6px">
                <Typography variant="body1" color={theme.palette.primary.main}>
                  {t("MY_CAFETERIA_OUT_OF_BOUNDS")}
                </Typography>
              </Box>
            )}
            {isOverlap === true && (
              <Box paddingLeft="6px">
                <Typography variant="body1" color={theme.palette.primary.main}>
                  {t("MY_CAFETERIA_APPLIED_BENEFIT")}
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
        {cafeteriaMenuData?.length === 0 && (
          <>
            <Box height="16px" />
            <Typography variant="body1" color={theme.palette.primary.main}>
              {t("MY_CAFETERIA_BENEFIT_ELEMENT_IS_NOT_DEFINED")}
            </Typography>
          </>
        )}
        <OcCardButtonBar
          handleLeftButton={handleDelete}
          leftButtonText={t("OC_DELETE")}
          leftButtonColor={inlineEdit ? "primary" : undefined}
          leftButtonId={`my-cafeteria-benefit-form-delete-button-${row ? row?.index : "zero"
            }`}
          leftButtonDisabled={buttonDisabled}
          handleRightButton={handleCancel}
          rightButtonText={t("OC_CANCEL")}
          rightButtonColor="secondary"
          rightButtonId={`my-cafeteria-benefit-form-cancel-button-${row ? row?.index : "zero"
            }`}
          handlePrimaryButton={inlineEdit ? handleUpdate : handleSave}
          primaryButtonText={inlineEdit ? t("OC_SAVE") : t("OC_ADD")}
          primaryButtonId={`my-cafeteria-benefit-form-add-button-${row ? row?.index : "zero"
            }`}
          primaryButtonDisabled={
            inlineEdit
              ? (newItem?.amount === row?.amount &&
                newItem?.szepCardApplicationAmount ===
                row?.szepCardApplicationAmount) ||
              availableAmountIndicator < 0 ||
              errorState.result ||
              !hasValidCalculation ||
              buttonDisabled
              : !isNewItemValid() ||
              cafeteriaMenuData?.length === 0 ||
              !hasValidCalculation ||
              buttonDisabled ||
              isOverlap
          }
        />
      </Box>
    </form>
  );
};

export default MyCafeteriaBenefitForm;
